'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { isAccountActive, isAccountDeactivated, normalizeAccountStatus } from '@/lib/auth/accountStatus';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

export default function LoginForm() {
  const searchParams = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam === 'account_inactive') {
      setError('Your account has been deactivated. Please contact your administrator.');
    }
  }, [searchParams]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const supabase = createClient();
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (signInError) {
        setError('Invalid email or password. Please try again.');
        setLoading(false);
        return;
      }

      // Check account status via SECURITY DEFINER RPCs (avoids RLS recursion).
      // Only treat explicit inactive/suspended as deactivated — never null/errors.
      if (data?.user) {
        const { data: accountStatus, error: statusError } = await supabase.rpc(
          'get_my_account_status'
        );

        if (statusError) {
          // Fallback: boolean helper (also SECURITY DEFINER)
          const { data: isActive, error: activeError } = await supabase.rpc(
            'is_account_active'
          );

          if (activeError) {
            await supabase.auth.signOut();
            setError('Unable to verify account status. Please try again.');
            setLoading(false);
            return;
          }

          if (isActive === false) {
            await supabase.auth.signOut();
            setError('Your account has been deactivated. Please contact your administrator.');
            setLoading(false);
            return;
          }

          // isActive === true or null with no error → allow login
          window.location.assign('/portal');
          return;
        }

        const status = normalizeAccountStatus(accountStatus);

        if (isAccountDeactivated(status)) {
          await supabase.auth.signOut();
          setError('Your account has been deactivated. Please contact your administrator.');
          setLoading(false);
          return;
        }

        if (!isAccountActive(status)) {
          // Missing/unknown status (often caused by RLS/RPC issues) — not "deactivated"
          await supabase.auth.signOut();
          setError('Unable to verify account status. Please try again.');
          setLoading(false);
          return;
        }
      }

      // Full navigation so middleware sees fresh auth cookies
      window.location.assign('/portal');
    } catch {
      setError('An unexpected error occurred. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-lg px-8 py-10 sm:px-10">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <div className="relative w-48 h-48 sm:w-56 sm:h-56">
                <Image
                  src="/assets/images/ksgh_logo-1786283980131.png"
                  alt="Kids Store GH"
                  fill
                  className="object-contain"
                  priority
                />
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSignIn} className="space-y-5">
              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                  {error}
                </div>
              )}

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#CC1B8A] focus:border-transparent transition"
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#CC1B8A] focus:border-transparent transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Sign In Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#CC1B8A] hover:bg-[#a8157a] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg text-sm transition-colors duration-200 mt-2"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>

              {/* Forgot Password */}
              <div className="text-center pt-1">
                <a href="/forgot-password" className="text-sm text-gray-500 hover:text-[#CC1B8A] transition-colors">
                  Forgot Password?
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-4 text-center">
        <p className="text-xs text-gray-400">
          © 2026 Kids Store GH. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
